import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { MoodboardItem, SaveToMoodboardPayload } from '@/types/moodboards';

export function useMoodboardItems(moodboardId?: string) {
  return useQuery({
    queryKey: ['moodboard-items', moodboardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('moodboard_items')
        .select('*')
        .eq('moodboard_id', moodboardId!)
        .order('position', { ascending: true });

      if (error) throw error;
      return data as MoodboardItem[];
    },
    enabled: !!moodboardId,
  });
}

export function useAddToMoodboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      moodboardId: string;
      item: SaveToMoodboardPayload;
    }) => {
      // Get the current max position
      const { data: existingItems } = await supabase
        .from('moodboard_items')
        .select('position')
        .eq('moodboard_id', params.moodboardId)
        .order('position', { ascending: false })
        .limit(1);

      const newPosition = existingItems && existingItems.length > 0 
        ? existingItems[0].position + 1 
        : 0;

      // Check if item already exists in moodboard
      const { data: existing } = await supabase
        .from('moodboard_items')
        .select('id')
        .eq('moodboard_id', params.moodboardId)
        .eq('content_type', params.item.content_type)
        .eq('content_id', params.item.content_id)
        .maybeSingle();

      if (existing) {
        toast({
          title: 'Already in moodboard',
          description: 'This item is already saved to this moodboard',
        });
        return null;
      }

      const { data, error } = await supabase
        .from('moodboard_items')
        .insert({
          moodboard_id: params.moodboardId,
          content_type: params.item.content_type,
          content_id: params.item.content_id,
          position: newPosition,
          image_url: params.item.image_url,
          author_id: params.item.author_id,
          author_username: params.item.author_username,
          caption: params.item.caption,
          original_created_at: params.item.original_created_at,
        })
        .select()
        .single();

      if (error) throw error;

      // Update moodboard's updated_at timestamp
      await supabase
        .from('moodboards')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', params.moodboardId);

      // Update cover if this is the first item
      if (newPosition === 0) {
        await supabase
          .from('moodboards')
          .update({
            cover_image_url: params.item.image_url,
            cover_content_type: params.item.content_type,
            cover_content_id: params.item.content_id,
          })
          .eq('id', params.moodboardId);
      }

      // Create notification for content author (if not saving own content)
      if (user && params.item.author_id && params.item.author_id !== user.id) {
        try {
          // Determine which ID to use based on content type
          const bulletinId = params.item.content_type === 'bulletin' ? params.item.content_id : null;
          const loopId = params.item.content_type === 'loop' ? params.item.content_id : null;

          await supabase
            .from('notifications')
            .insert({
              user_id: params.item.author_id,
              actor_id: user.id,
              type: 'save',
              bulletin_id: bulletinId,
              loop_id: loopId,
            });
        } catch (notificationError) {
          console.error('Error creating save notification:', notificationError);
          // Don't fail the save if notification fails
        }
      }

      return data as MoodboardItem;
    },
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['moodboard-items', variables.moodboardId] });
        queryClient.invalidateQueries({ queryKey: ['moodboards'] });
        queryClient.invalidateQueries({ queryKey: ['moodboard', variables.moodboardId] });
        toast({
          title: 'Added to moodboard',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to add to moodboard',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRemoveFromMoodboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { itemId: string; moodboardId: string }) => {
      const { error } = await supabase
        .from('moodboard_items')
        .delete()
        .eq('id', params.itemId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['moodboard-items', variables.moodboardId] });
      queryClient.invalidateQueries({ queryKey: ['moodboards'] });
      toast({
        title: 'Removed from moodboard',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove from moodboard',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useReorderMoodboardItems() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      moodboardId: string;
      items: { id: string; position: number }[];
    }) => {
      const updates = params.items.map(item =>
        supabase
          .from('moodboard_items')
          .update({ position: item.position })
          .eq('id', item.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      
      if (errors.length > 0) {
        throw new Error('Failed to reorder items');
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['moodboard-items', variables.moodboardId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to reorder items',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useCheckInMoodboard(moodboardId?: string, contentType?: string, contentId?: string) {
  return useQuery({
    queryKey: ['moodboard-check', moodboardId, contentType, contentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('moodboard_items')
        .select('id')
        .eq('moodboard_id', moodboardId!)
        .eq('content_type', contentType!)
        .eq('content_id', contentId!)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!moodboardId && !!contentType && !!contentId,
  });
}
